import { VoteResponseDto } from '@api/dto/data-contracts';
import {
  IVoteList,
  IVoteResponse,
  TVoteListStatus,
  TVoteResponseSortField,
} from '@contexts/VoteContext/types';
import { faker } from '@faker-js/faker';

class Space {
  id;
  name;
  symbol;
  description;
  constructor() {
    const spaceName = `Space No.${faker.number.int({ max: 10000 })}`;
    this.id = faker.string.uuid();
    this.name = spaceName;
    this.symbol = faker.image.urlPlaceholder({
      height: 640,
      width: 640,
      text: spaceName,
    });
    this.description = faker.commerce.productDescription();
  }

  get() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      symbol: this.symbol,
    };
  }
}

export const space = new Space();

class VoteListData {
  currentPage = 1;
  initalData;
  data;
  totalElementCount;
  totalPage;
  limit;
  constructor({ size }: { size: number }) {
    this.initalData = Array.from({ length: size }, this.createMockVoteListData);
    this.data = this.initalData;
    this.totalElementCount = size;
    this.totalPage = Math.ceil(size / this.limit);
  }

  getRandomStatus() {
    const statuses = ['WAITING', 'IN_PROGRESS', 'COMPLETED'];
    return statuses[
      Math.floor(Math.random() * statuses.length)
    ] as VoteResponseDto['status'];
  }

  createMockVoteListData = (): IVoteList => {
    return {
      id: faker.string.uuid(),
      description: faker.commerce.productDescription(),
      descriptionImageUrl: faker.image.url(),
      endDate: faker.date.recent().toISOString(),
      name: faker.commerce.productName(),
      spaceId: faker.string.uuid(),
      startDate: faker.date.recent().toISOString(),
      status: this.getRandomStatus(),
      voteCount: faker.number.int({ min: 10, max: 100 }),
      createdAt: faker.date.recent().toISOString(),
      totalParticipantCount: faker.number.int({ min: 0, max: 100 }),
      totalVoteCount: faker.number.int({ min: 0, max: 100 }),
      space: {
        description: faker.commerce.productDescription(),
        id: faker.string.uuid(),
        name: faker.commerce.productName(),
        symbolImageUrl: faker.image.url(),
      },
    };
  };

  get({ requestPageNumber }: { requestPageNumber: number }) {
    const startIndex = (requestPageNumber - 1) * this.limit;
    const endIndex = startIndex + this.limit;

    return {
      currentPage: requestPageNumber,
      data: this.data.slice(startIndex, endIndex),
      totalElementCount: this.totalElementCount,
      totalPage: this.totalPage,
    };
  }

  getById({ voteId }: { voteId: string }) {
    return this.initalData.filter((item) => item.id === voteId)[0];
  }

  set({
    limit,
    status,
    search,
  }: {
    limit: number;
    status: TVoteListStatus;
    search?: string;
  }) {
    let result = this.initalData;
    if (search) {
      result = result.filter((item) => item.name === search);
    }

    if (status === 'IN_PROGRESS') {
      this.data = this.initalData.filter(
        (item) => item.status === 'IN_PROGRESS',
      );
    } else if (status === 'COMPLETED') {
      this.data = result.filter((item) => item.status === 'COMPLETED');
    } else if (status === 'WAITING') {
      this.data = result.filter((item) => item.status === 'WAITING');
    } else {
      this.data = result;
    }
    this.limit = limit;
    this.totalElementCount = this.data.length;
    this.totalPage = Math.ceil(this.totalElementCount / this.limit);
  }
}

export const voteListData = new VoteListData({ size: 39 });

class VoteResponseData {
  currentPage = 1;
  initalData;
  data;
  totalElementCount;
  totalPage;
  limit;
  responses = ['YES', 'NO', 'No Idea'];
  constructor({ size }: { size: number }) {
    this.initalData = Array.from({ length: size }, this.createMock);
    this.data = this.initalData;
    this.totalElementCount = size;
    this.totalPage = Math.ceil(size / this.limit);
  }

  getRandomAnswer() {
    return this.responses[Math.floor(Math.random() * this.responses.length)];
  }

  createMock = (): IVoteResponse => {
    return {
      voteName: faker.commerce.productName(),
      answerName: this.getRandomAnswer(),
      createdAt: faker.date.recent().toISOString(),
      voteCount: faker.number.int({ min: 10, max: 100 }),
      voterId: faker.string.uuid(),
      voterNickname: faker.person.firstName(),
    };
  };

  get({ requestPageNumber }: { requestPageNumber: number }) {
    const startIndex = (requestPageNumber - 1) * this.limit;
    const endIndex = startIndex + this.limit;

    return {
      currentPage: requestPageNumber,
      data: this.data.slice(startIndex, endIndex),
      totalElementCount: this.totalElementCount,
      totalPage: this.totalPage,
    };
  }

  getStatistics() {
    const statistics = this.responses.map((response) => {
      let voteCount = 0;
      this.initalData.map((item) => {
        if (item.answerName === response) {
          voteCount += item.voteCount;
        }
      });
      return {
        name: response,
        voteCount,
      };
    });
    return statistics;
  }

  getStatisticsForDashboard() {
    const statistics = Array.from({ length: 4 }, () => ({
      answers: Array.from(
        { length: faker.number.int({ min: 2, max: 5 }) },
        () => ({
          name: faker.person.firstName(),
          voteCount: faker.number.int({ min: 10, max: 100 }),
        }),
      ),
      name: faker.person.firstName(),
      voteId: faker.string.uuid(),
    }));
    return {
      data: statistics,
    };
  }

  set({ limit, status }: { limit: number; status?: TVoteResponseSortField }) {
    if (status === 'MOST_VOTE_COUNT') {
      this.data = this.initalData.sort((a, b) => {
        const voteCountA = a.voteCount;
        const voteCountB = b.voteCount;

        if (isNaN(voteCountA) || isNaN(voteCountB)) {
          throw new Error('Invalid Number');
        }

        return voteCountB - voteCountA;
      });
    } else if (status === 'RECENT') {
      this.data = this.initalData.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);

        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          throw new Error('Invalid date format');
        }

        return dateB.getTime() - dateA.getTime();
      });
    } else {
      this.data = this.initalData;
    }
    this.limit = limit;
    this.totalElementCount = this.data.length;
    this.totalPage = Math.ceil(this.totalElementCount / this.limit);
  }
}

export const voteResponseData = new VoteResponseData({ size: 39 });
